import { useEffect, useRef } from 'react';
import { useEnrollQueue } from '../../EnrollQueue/EnrollQueueContext';
import { useShellFrameStore } from '../shell-frame-store';
import { useFlowPanelsStore, openPanel, closePanel } from './flow-panels-store';

/**
 * useActionsDockSync — ONE source of truth for "is Actions open" on `/flow/*`
 * (plan `2026-10-06-002-v2-qa-followups-implementation-plan.md`, U2/R6, Key
 * Technical Decision 3). Two flags used to drift: the panel store's
 * `actionsOpen` (`flow-panels-store.ts`, what paints the Flow shell's Actions
 * column and lights the Header's lightning toggle) and the enroll-queue
 * dock's own `isOpen` (`EnrollQueue/useQueueUiSignals.ts:174`, what decides
 * whether `rail/RailDock.tsx` renders at all). The Header's toggle wrote only
 * the first; every real producer — `openQueueTab` (`ContactEnrollTrigger`),
 * `openCustomInvite` (`PlaysTab`), `openRequestDetail`
 * (`EntityStateFactCard`/`ActionsIndex`), and `useDockUrlSync`'s `?request=`
 * deep link — writes only the second, which is why the rail used to pop open
 * with the lightning icon unlit. This hook makes either one imply the other.
 *
 * The two stores are NOT merged: `useQueueUiSignals`' `isOpen` also feeds
 * `rightOwner` and `useDockUrlSync`'s WRITE effect, so a merge is a much
 * larger refactor (KTD 3's rejected alternative).
 *
 * ── Loop safety: deps are TRIGGERS, truth is read LIVE ────────────────────
 * Copied verbatim in intent from `panels/useFlowPanelsUrlSync.ts`: each effect
 * below lists only what should make it RE-RUN, and its body reads the current
 * truth through `useFlowPanelsStore.getState()` (panel store) and a latest-ref
 * (the dock's `isOpen`/`open`/`close`, reassigned every render). A body that
 * read the render's closure values instead would act on pre-commit state and
 * the pair would oscillate. Both effects are also no-ops when the two sides
 * already agree, which is what stops a legitimate write from bouncing back.
 * `wasOpenRef` is the one piece of state the hook owns, and it is a
 * previous-value tracker, not a lifetime flag — it is rewritten on every run,
 * so it needs no mount-time reset.
 *
 * ── Why Effect A mirrors only a TRANSITION to closed, and is declared FIRST ─
 * A (dock -> panel) opens the panel whenever it sees the dock open, but closes
 * it only when it OBSERVES `isOpen` go true -> false (`wasOpenRef`). Two
 * reasons, both from Codex round 2 (plan 2026-10-06-004, Area: Sidebar
 * Dynamics, Unit A):
 *  1. A dock close can still happen without the Header's toggle (any
 *     `useEnrollQueue().close()` caller). Unmirrored, that leaves
 *     `actionsOpen` true over a dock that has unmounted — an empty Actions
 *     column under a lit toggle, and a collapsed Flow that could not be
 *     reopened (the toggles live in that column whenever Actions is open in
 *     push mode, see `rail/RailDock.tsx`). Mirroring the transition closes
 *     the panel too.
 *  2. On a cold `/flow?panels=actions` load, a mirror of the closed STATE
 *     would race `useFlowPanelsUrlSync`'s READ of `?panels=` (sibling
 *     components, no ordering guarantee between their effects) and slam shut
 *     the panel the deep link just opened. A TRANSITION cannot be observed on
 *     mount — the ref is seeded with the first render's value — so a
 *     late-running A can only ever open there, never undo the READ.
 * B's own close (panel -> dock) also flips `isOpen` true -> false; A then
 * reads `actionsOpen` live, finds it already false, and no-ops — no ping-pong.
 * Declaration order is load-bearing: on entering `/flow` with the floating
 * dock already open and `actionsOpen` false, A writes `actionsOpen` true
 * synchronously (zustand `setState`), and B — running later in the SAME
 * commit, reading `getState()` — sees the fresh value and no-ops. Swap the
 * two and B would close the dock before A could open the panel: a visible
 * close/reopen flicker.
 *
 * ── Outside the frame (Delphi) only Effect C runs ─────────────────────────
 * Effects A and B bail while `useShellFrameStore().frameActive` is false (set by
 * `ShellLayout`'s framed branch), so Delphi — the one frameless route — keeps
 * `RailDock`'s fallback `fixed` aside with its own close-X and no panel column
 * to agree with. Every framed route (Home, Library, My Actions, `/tools/*`,
 * `/flow/*`) mounts `FlowPanels`, so the same two-way sync applies everywhere.
 * The trigger is a zustand store, NOT a context and NOT `actionsNode !== null`:
 * this hook mounts in `RailDock` via `EnrollQueueDock`, a SIBLING of the
 * `<Outlet/>` holding `ShellLayout`, so no provider there can reach it, and
 * the slot node is null whenever the column is merely CLOSED, which would
 * disable Effect A's dock -> panel open.
 *
 * ── Suppressed Actions (hideEnrollQueue apps) ─────────────────────────────
 * While the panel store's `actionsSuppressed` is set, `openPanel('actions')`
 * is a no-op, so Effect A closes the dock again instead of leaving it open
 * with no column (Effect B would not re-run: `actionsOpen` did not change).
 *
 * The hook is mounted in `RailDock` (not `FlowShell`) because that is the one
 * component mounted under V2 regardless of route that already has
 * `useEnrollQueue()` in scope (KTD 4).
 *
 * ── Durable record (R16) ─────────────────────────────────────────────────
 * `.baton/BATON.md` item #17 ("two independent open flags for Actions") is
 * resolved by this hook — that file is gitignored, so this comment is the
 * durable record. The dock's Chat tab has since been retired: chat lives only
 * in its own panel, and its deep link opens that panel.
 */
export function useActionsDockSync(): void {
  const framed = useShellFrameStore((s) => s.frameActive);
  const { isOpen, open, close } = useEnrollQueue();

  // Latest-ref, reassigned every render: the effects below must act on the
  // dock's CURRENT state and callbacks without taking either as a dependency.
  const dockRef = useRef({ isOpen, open, close });
  dockRef.current = { isOpen, open, close };

  // Subscription only — see the "truth is read live" note above. `isOpen`
  // likewise appears in Effect A's deps purely as its trigger.
  const actionsOpen = useFlowPanelsStore((s) => s.actionsOpen);

  // Previous-value tracker for Effect A's close-mirror. Seeded with the FIRST
  // render's value so the mount run — and StrictMode's setup→cleanup→setup —
  // can never observe a transition; updated unconditionally at the top of
  // every run, INCLUDING outside the frame, so a dock closed on Delphi (fallback X) is
  // not replayed as a close the moment the frame mounts.
  const wasOpenRef = useRef(isOpen);

  // Effect A — dock -> panel. Declared FIRST (see above). Opens on an open
  // state; closes ONLY on an observed open->closed transition.
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (!framed) return;
    const live = useFlowPanelsStore.getState().actionsOpen;
    if (isOpen && !live) {
      openPanel('actions');
      if (useFlowPanelsStore.getState().actionsSuppressed) dockRef.current.close();
    }
    else if (!isOpen && wasOpen && live) closePanel('actions');
    // `isOpen`/`framed` are the triggers; the panel store is read live via
    // `getState()` and is deliberately NOT a dependency (the loop-safety rule
    // above). No `oxlint-disable` is needed for that: a `getState()` read is
    // not a reactive value, so neither linter's exhaustive-deps flags this
    // array — unlike `useDockUrlSync.ts:150`, whose array really does omit
    // reactive values and therefore carries the scoped directive.
  }, [isOpen, framed]);

  // Effect B — panel -> dock, BOTH directions. The Header's lightning toggle,
  // the chooser's Act row, and (after U3) the panel's only close path all
  // land here.
  useEffect(() => {
    if (!framed) return;
    const live = useFlowPanelsStore.getState().actionsOpen;
    const dock = dockRef.current;
    if (live && !dock.isOpen) dock.open();
    else if (!live && dock.isOpen) dock.close();
    // Same rule: `actionsOpen`/`framed` are the triggers, and the dock's state
    // and callbacks are read through `dockRef` — a ref read is exempt from
    // exhaustive-deps, so this array needs no directive either.
  }, [actionsOpen, framed]);

  // Effect C — unframed (Delphi) suppression. Frameless there is no panel
  // column to agree with, so a `shell.hideEnrollQueue` app (suppressed by
  // `ShellLayout`) just closes the dock when it is open or a producer opens
  // it, instead of leaving it open behind CSS. Framed, Effects A/B own this.
  const suppressed = useFlowPanelsStore((s) => s.actionsSuppressed);
  useEffect(() => {
    if (framed) return;
    if (suppressed && dockRef.current.isOpen) dockRef.current.close();
  }, [framed, suppressed, isOpen]);
}
